import { useIdGraphQl } from '@hooks/graphql/useIdGraphQl';
import { METER_CORE_INFO, METER_LATEST_READ_TYPE, UPDATE_METER_CORE_INFO, GET_IPP_BY_VENDOR_NAME } from '@graphql/meterCoreInfo.gql';
import { CREATE_IPP_VENDOR } from '@graphql/vendorList.gql';
import useSession from '@hooks/useSession';
import { Form, App } from 'antd';
import { useEffect, useCallback, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';


export const useCoreInfoPage = () => {
  const { isUser, isSuperAdmin } = useSession();
  const canEdit = isUser || isSuperAdmin;
  const [form] = Form.useForm();
  const { modal, message } = App.useApp();

  const {
    data: fetchData,
    loading,
    updating,
    fetchError,
    updateError,
    updateResponse,
    setUpdateVariables,
    setQueryVariables,
    id,
  } = useIdGraphQl(METER_CORE_INFO, UPDATE_METER_CORE_INFO, undefined, 'meterId');

  const [ippLookupLoading, setIppLookupLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);


  const resolvedIppIdRef = useRef<number | null | undefined>(undefined);

  const { data: readTypeData } = useQuery(METER_LATEST_READ_TYPE, {
    variables: { meterId: Number(id) },
    skip: !id,
  });

  const isNewMeter = readTypeData?.meterLatestReadType?.toLowerCase() === 'new';

  useEffect(() => {
    if (updateError || fetchError) {
      message.error(updateError?.message || fetchError?.message);
      setSaveSuccess(false);
    }
  }, [updateError, fetchError, message]);

  useEffect(() => {
    if (fetchData) {
      setData(fetchData.meterCoreInfo);
    }
  }, [fetchData]);

  useEffect(() => {
    if (updateResponse) {
      setData(updateResponse.updateMeterCoreInfo);
      setSaveSuccess(true);
    }
  }, [updateResponse]);

  useEffect(() => {
    if (data) {
      resolvedIppIdRef.current = data.ipp?.ippId ?? null;

      form.setFieldsValue({
        ...data,
        // This page always treats CONTRACT as the selected rate type (radio selection
        // removed). data.selectedRateType reflects meter_master.rate_id from the server,
        // which is null/other until the first save — without this override it would
        // clobber the tariff section's own 'CONTRACT' default and the save would submit
        // selectedRateType: null, leaving meter_master.rate_id unset.
        selectedRateType: 'CONTRACT',
        operationalStatus: data.operationalStatus ?? 'Active',
        retroactiveReminder: data.retroactiveReminder ? dayjs(data.retroactiveReminder) : undefined,
        operationalStatusDate: data.operationalStatusDate ? dayjs(data.operationalStatusDate) : undefined,
        rateContracts: (data.rateContracts ?? []).map((rc) => ({
          ...rc,
          effectiveFrom: rc.effectiveFrom ? dayjs(rc.effectiveFrom) : undefined,
          effectiveTo: rc.effectiveTo ? dayjs(rc.effectiveTo) : undefined,
        })),
      });
    }
  }, [data, form]);

  const [createVendor] = useMutation(CREATE_IPP_VENDOR);

  const [fetchIppByVendor] = useLazyQuery(GET_IPP_BY_VENDOR_NAME, {
    fetchPolicy: 'network-only',
    onCompleted: (queryData) => {
      const ipp = queryData?.ippByVendorName;
      //   Write to ref — null if not found, number if found
      resolvedIppIdRef.current = ipp?.ippId ?? null;

      if (ipp) {
        // Vendor found — also hydrate form fields with DB values
        form.setFieldsValue({
          ipp: {
            ...form.getFieldValue('ipp'),
            vendorName: ipp.vendorName,
          },
        });
      }
      setIppLookupLoading(false);
    },
    onError: () => {
      resolvedIppIdRef.current = null;
      setIppLookupLoading(false);
    },
  });

  const handleVendorBlur = useCallback(() => {
    const vendorName = form.getFieldValue(['ipp', 'vendorName'])?.trim();
    if (!vendorName) return;
    // Reset ref so we know a fresh lookup is pending
    resolvedIppIdRef.current = undefined;
    setIppLookupLoading(true);
    fetchIppByVendor({ variables: { vendorName } });
  }, [form, fetchIppByVendor]);


  const lookupVendorNow = useCallback((vendorName: string): Promise<number | null> => {
    return new Promise((resolve) => {
      resolvedIppIdRef.current = undefined;
      setIppLookupLoading(true);
      fetchIppByVendor({ variables: { vendorName } });

      const interval = setInterval(() => {
        if (resolvedIppIdRef.current !== undefined) {
          clearInterval(interval);
          resolve(resolvedIppIdRef.current);
        }
      }, 50);

      setTimeout(() => {
        clearInterval(interval);
        resolve(resolvedIppIdRef.current ?? null);
      }, 10000);
    });
  }, [fetchIppByVendor]);

  const _doSave = useCallback(
    (values: any, overrideIppId?: number | null) => {
      const ippId = overrideIppId === undefined ? resolvedIppIdRef.current : overrideIppId;

      setUpdateVariables({
        input: {
          meterId: values.meterId,
          meterName: values.meterName,
          energy: values.energy,
          region: values.region,
          statGl: values.statGl?.glNumber ? { glNumber: values.statGl.glNumber } : null,
          dollarGl: values.dollarGl?.glNumber ? { glNumber: values.dollarGl.glNumber } : null,
          ipp: values.ipp
            ? {
              ...values.ipp,
              ippId: ippId ? Number(ippId) : undefined,
            }
            : null,
          poLineNumber: values.poLineNumber != null && values.poLineNumber !== ''
            ? Number(values.poLineNumber)
            : null,
          meterType: values.meterType,
          meterFileSource: values.meterFileSource,
          kwhGenerated: values.kwhGenerated?.replace(/^Channel\s+/i, '') ?? values.kwhGenerated,
          kwhUsed: values.kwhUsed?.replace(/^Channel\s+/i, '') ?? values.kwhUsed,
          retroactiveReminder: values.retroactiveReminder
            ? values.retroactiveReminder.format('YYYY-MM-DD')
            : null,
          energyBid: values.energyBid ? Number(values.energyBid) : null,
          piTagNameArgControlStatus: values.piTagNameArgControlStatus,
          piTagNameMegawattValue: values.piTagNameMegawattValue || null,
          lossFactor: values.lossFactor ? Number(values.lossFactor) : null,
          multiplier: values.multiplier ? Number(values.multiplier) : null,
          operationalStatus: values.operationalStatus || null,
          operationalStatusDate: values.operationalStatusDate?.isValid()
            ? values.operationalStatusDate.format('YYYY-MM-DD')
            : null,
          applyHst: values.applyHst === true,
          adminFeeRate: values.adminFeeRate != null && values.adminFeeRate !== '' ? Number(values.adminFeeRate) : null,
          forecastFee: values.forecastFee != null && values.forecastFee !== '' ? Number(values.forecastFee) : null,
          forecastFeeCurrency: values.forecastFeeCurrency || null,
          forecastFeeGl: values.forecastFeeGl?.glNumber ? { glNumber: values.forecastFeeGl.glNumber } : null,
          adminFeeGl: values.adminFeeGl?.glNumber ? { glNumber: values.adminFeeGl.glNumber } : null,
          rateContracts: (values.rateContracts ?? []).map((rc) => ({
            rateId: rc.rateId,
            rateType: rc.rateType,
            effectiveFrom: rc.effectiveFrom?.isValid() ? rc.effectiveFrom.format('YYYY-MM-01') : null,
            effectiveTo: rc.effectiveTo?.isValid() ? rc.effectiveTo.format('YYYY-MM-01') : null,
            rate: rc.rate ? Number(rc.rate) : null,
          })),
          selectedRateId: values.selectedRateId ?? null,
          selectedRateType: values.selectedRateType,
        },
      });
    },
    [setUpdateVariables]
  );

  // Called by AntD form submit (htmlType="submit") — validation already passed
  const onFinish = useCallback(
    async (values: any) => {
      // Extra cross-field validation for rate contracts
      for (const rc of values.rateContracts ?? []) {
        if (rc.effectiveFrom && rc.effectiveTo && rc.effectiveFrom.isAfter(rc.effectiveTo)) {
          message.error(`Effective From cannot be after Effective To for rate type: ${rc.rateType}`);
          return;
        }
      }

      if (ippLookupLoading) {
        message.info('Please wait for vendor lookup to complete');
        return;
      }

      const vendorName = values.ipp?.vendorName?.trim();

      if (!vendorName) {
        _doSave(values);
        return;
      }

      // If never blurred, trigger lookup now and wait
      let resolvedId = resolvedIppIdRef.current;
      if (resolvedId === undefined) {
        message.loading({ content: 'Looking up vendor…', key: 'vendor-lookup' });
        resolvedId = await lookupVendorNow(vendorName);
        message.destroy('vendor-lookup');
      }

      // Vendor found — save directly
      if (resolvedId !== null && resolvedId !== undefined) {
        _doSave(values, resolvedId);
        return;
      }

      // Vendor NOT found — confirm before creating
      modal.confirm({
        title: 'Vendor Not Found',
        content: `The vendor "${vendorName}" was not found in the database. If you proceed, a new vendor will be created with this name and the meter will be saved.`,
        okText: 'Yes, create vendor & save',
        cancelText: 'No, go back',
        onOk: async () => {
          try {
            const result = await createVendor({ variables: { input: { vendorName } } });
            const newIppId = result.data?.createIppVendor?.id;
            if (!newIppId) {
              message.error(`Failed to create vendor "${vendorName}". Please try again.`);
              throw new Error('No ippId returned');
            }
            resolvedIppIdRef.current = newIppId;
            _doSave(values, newIppId);
          } catch (err: any) {
            message.error(err?.message || 'An error occurred while creating the vendor.');
            throw err;
          }
        },
      });
    },
    [form, ippLookupLoading, createVendor, _doSave, modal, message, lookupVendorNow]
  );

  return {
    data,
    loading,
    updating,
    fetchError,
    updateError,
    updateResponse,
    ippLookupLoading,
    handleVendorBlur,
    setUpdateVariables,
    setQueryVariables,
    id,
    canEdit,
    form,
    onFinish,
    saveSuccess,
    setSaveSuccess,
    isNewMeter,
  };
};
